
import React, { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useMessaging } from '../contexts/MessagingProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import type { User, Message } from '../types';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import NewMessageModal from './NewMessageModal';
import EditIcon from './icons/EditIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import * as api from '../services/apiService';

const MessagesPage: React.FC = () => {
  const { currentUser, users } = useAuth();
  const { messages, activeConversationPartner, setActiveConversationPartner, handleSendMessage, isNewMessageModalOpen, handleOpenNewMessageModal, handleCloseNewMessageModal, markMessagesAsReadLocally } = useMessaging();
  const { handleNavigation } = useNavigation();
  const [activeTab, setActiveTab] = useState<'inbox' | 'requests'>('inbox');
  
  const userMap = useMemo(() => new Map(users.map(u => [u.handle, u])), [users]);
  
  const tier = currentUser?.subscriptionTier;
  const isPro = tier && tier !== 'basic';
  
  const handleSelectConversation = useCallback(async (user: User | null) => {
    if (user === null) {
        setActiveConversationPartner(null);
        return;
    }
    setActiveConversationPartner(user);
    // Optimistically update local state so the badge disappears immediately
    markMessagesAsReadLocally(user.handle);
    try {
        await api.markConversationAsRead(user.handle);
    } catch (error) {
        console.error("Failed to mark conversation as read on server:", error);
    }
  }, [setActiveConversationPartner, markMessagesAsReadLocally]);

  const { allConversations, inboxConversations, requestConversations } = useMemo(() => {
    if (!currentUser) return { allConversations: [], inboxConversations: [], requestConversations: [] };
    
    const conversationsMap = new Map<string, { partner: User, lastMessage: Message }>();
    messages.forEach(msg => {
      const partnerHandle = msg.senderHandle === currentUser.handle ? msg.receiverHandle : msg.senderHandle;
      const existing = conversationsMap.get(partnerHandle);
      if (!existing || new Date(msg.timestamp) > new Date(existing.lastMessage.timestamp)) {
        const partner = userMap.get(partnerHandle);
        if (partner) {
          conversationsMap.set(partnerHandle, { partner, lastMessage: msg });
        }
      }
    });

    const all = Array.from(conversationsMap.values()).map(c => ({
      partner: c.partner,
      lastMessage: c.lastMessage.text,
      timestamp: c.lastMessage.timestamp,
    }));

    if (!isPro) {
      return { allConversations: all, inboxConversations: [], requestConversations: [] };
    }

    const networkHandles = new Set([...(currentUser.endorsements || []), ...(currentUser.endorsedBy || [])]);
    const inbox = all.filter(c => networkHandles.has(c.partner.handle));
    const requests = all.filter(c => !networkHandles.has(c.partner.handle));

    return { allConversations: [], inboxConversations: inbox, requestConversations: requests };

  }, [messages, currentUser, userMap, isPro]);


  const activeConversationMessages = React.useMemo(() => {
    if (!currentUser || !activeConversationPartner) return [];
    return messages.filter(
      msg =>
        (msg.senderHandle === currentUser.handle && msg.receiverHandle === activeConversationPartner.handle) ||
        (msg.senderHandle === activeConversationPartner.handle && msg.receiverHandle === currentUser.handle)
    ).sort((a,b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [messages, currentUser, activeConversationPartner]);

  if (!currentUser) return null;

  const showChatWindow = !!activeConversationPartner;
  const conversationsForList = isPro ? (activeTab === 'inbox' ? inboxConversations : requestConversations) : allConversations;

  const renderConversationList = () => (
     <div className="flex-grow overflow-y-auto">
        <ConversationList
            conversations={conversationsForList}
            activePartnerHandle={activeConversationPartner?.handle || null}
            onSelectConversation={handleSelectConversation}
        />
    </div>
  );

  const renderTabs = () => (
    <div className="flex-shrink-0 px-4 border-b border-gray-700/50">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
            <TabButton label="Inbox" count={inboxConversations.length} isActive={activeTab === 'inbox'} onClick={() => setActiveTab('inbox')} />
            <TabButton label="Requests" count={requestConversations.length} isActive={activeTab === 'requests'} onClick={() => setActiveTab('requests')} />
        </nav>
    </div>
  );
  
  const handleNewMessage = (user: User) => {
    handleCloseNewMessageModal();
    handleSelectConversation(user);
    handleNavigation('messages');
  }

  return (
    <div className="flex h-full">
      <div className={`w-full md:hidden flex flex-col h-full ${showChatWindow ? 'hidden' : 'block'}`}>
         <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-700/50">
            <h1 className="text-2xl font-bold">Messages</h1>
            <button onClick={handleOpenNewMessageModal} className="p-2 rounded-full hover:bg-gray-700">
                <EditIcon className="h-6 w-6"/>
            </button>
        </div>
        {isPro && renderTabs()}
        {renderConversationList()}
      </div>
      
      {showChatWindow && (
        <div className="w-full md:hidden flex flex-col h-full">
           <button onClick={() => handleSelectConversation(null)} className="md:hidden flex items-center space-x-2 p-4 text-brand-muted hover:text-white font-semibold border-b border-gray-700/50">
              <ArrowLeftIcon className="h-5 w-5" />
              <span>All Conversations</span>
           </button>
           <ChatWindow
            partner={activeConversationPartner}
            messages={activeConversationMessages}
            onSendMessage={handleSendMessage}
           />
        </div>
      )}

      <div className="hidden md:flex w-full h-full gap-4">
        <aside className="w-1/3 flex-shrink-0 flex flex-col bg-brand-surface rounded-lg p-4">
           <div className="flex-shrink-0 flex justify-between items-center pb-4 border-b border-gray-700/50">
            <h1 className="text-2xl font-bold">Messages</h1>
            <button onClick={handleOpenNewMessageModal} className="p-2 rounded-full hover:bg-gray-700">
                <EditIcon className="h-6 w-6"/>
            </button>
        </div>
         {isPro && (
            <div className="flex-shrink-0 pt-4 border-b border-gray-700/50">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                    <TabButton label="Inbox" count={inboxConversations.length} isActive={activeTab === 'inbox'} onClick={() => setActiveTab('inbox')} />
                    <TabButton label="Requests" count={requestConversations.length} isActive={activeTab === 'requests'} onClick={() => setActiveTab('requests')} />
                </nav>
            </div>
         )}
        <div className="flex-grow overflow-y-auto pt-4">
            <ConversationList
                conversations={conversationsForList}
                activePartnerHandle={activeConversationPartner?.handle || null}
                onSelectConversation={handleSelectConversation}
            />
        </div>
        </aside>
        <main className="w-2/3 flex flex-col">
          {activeConversationPartner ? (
            <ChatWindow
              partner={activeConversationPartner}
              messages={activeConversationMessages}
              onSendMessage={handleSendMessage}
            />
          ) : (
            <div className="flex flex-grow items-center justify-center h-full bg-brand-surface rounded-lg text-brand-muted">
              <p>Select a conversation to start chatting</p>
            </div>
          )}
        </main>
      </div>

      {isNewMessageModalOpen && <NewMessageModal onClose={handleCloseNewMessageModal} onSelectUser={handleNewMessage} />}
    </div>
  );
};

const TabButton: React.FC<{ label: string; count: number; isActive: boolean; onClick: () => void }> = ({ label, count, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
            isActive
            ? 'border-brand-orange text-brand-orange'
            : 'border-transparent text-brand-muted hover:text-white hover:border-gray-500'
        }`}
    >
        {label} <span className="bg-gray-700/80 text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
    </button>
);

export default MessagesPage;
